import { Controller } from "@hotwired/stimulus";
import { subscribeToAttachment } from "../channels/attachment_channel";

export default class extends Controller {
  static targets = ["image", "status"];
  static values = {
    attachmentId: String,
    processed: Boolean,
  };

  subscription = null;

  connect() {
    console.log("[AttachmentProcessor] Controller connected");
    console.log("[AttachmentProcessor] Attachment ID:", this.attachmentIdValue);
    console.log("[AttachmentProcessor] Processed:", this.processedValue);

    // Only subscribe if the attachment is not yet processed
    if (this.hasAttachmentIdValue && !this.processedValue) {
      console.log(
        "[AttachmentProcessor] Attachment is pending, subscribing to updates",
      );
      this.subscribeToUpdates();
      this.showProcessingStatus();
    } else {
      console.log(
        "[AttachmentProcessor] Attachment already processed or no ID, skipping subscription",
      );
    }
  }

  disconnect() {
    console.log("[AttachmentProcessor] Controller disconnecting");
    this.unsubscribeFromUpdates();
  }

  subscribeToUpdates() {
    if (this.subscription) {
      console.log("[AttachmentProcessor] Already subscribed");
      return;
    }

    this.subscription = subscribeToAttachment(this.attachmentIdValue, {
      connected: () => {
        console.log("[AttachmentProcessor] Connected to attachment channel");
      },
      disconnected: () => {
        console.log(
          "[AttachmentProcessor] Disconnected from attachment channel",
        );
      },
      received: (data) => {
        console.log("[AttachmentProcessor] Received update:", data);
        this.handleAttachmentProcessed(data);
      },
    });
  }

  unsubscribeFromUpdates() {
    if (this.subscription) {
      console.log(
        "[AttachmentProcessor] Unsubscribing from attachment channel",
      );
      this.subscription.unsubscribe();
      this.subscription = null;
    }
  }

  handleAttachmentProcessed(data) {
    if (
      data.action === "processed" &&
      data.attachment_id === this.attachmentIdValue
    ) {
      console.log(
        "[AttachmentProcessor] Attachment processing complete, updating image",
      );

      // Update the image source with a cache-busting parameter
      if (this.hasImageTarget && data.large_url) {
        const timestamp = new Date().getTime();
        const newUrl = `${data.large_url}?t=${timestamp}`;
        console.log("[AttachmentProcessor] Updating image to:", newUrl);

        // Fade out, change image, fade in for a smooth transition
        this.imageTarget.style.transition = "opacity 0.3s ease-in-out";
        this.imageTarget.style.opacity = "0.5";

        setTimeout(() => {
          this.imageTarget.src = newUrl;
          this.imageTarget.style.opacity = "1";

          // Update data attribute to mark as processed
          this.processedValue = true;
        }, 300);
      }

      // Hide the processing status
      this.hideProcessingStatus();

      // Show success message
      this.showSuccessMessage();

      // Unsubscribe since we're done
      this.unsubscribeFromUpdates();
    }
  }

  showProcessingStatus() {
    if (this.hasStatusTarget) {
      // Replace the image with the processing GIF if we have an image target
      if (this.hasImageTarget) {
        this.imageTarget.src = "/images/bottle-preparing-image.gif";
        this.imageTarget.style.opacity = "1";
      }

      this.statusTarget.innerHTML = `
        <div class="absolute top-0 left-0 right-0 bg-blue-500 bg-opacity-90 text-white text-xs py-1 px-2 text-center">
          <div class="flex items-center justify-center space-x-2">
            <svg class="animate-spin h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>Processing image...</span>
          </div>
        </div>
      `;
    }
  }

  hideProcessingStatus() {
    if (this.hasStatusTarget) {
      this.statusTarget.style.transition = "opacity 0.3s ease-in-out";
      this.statusTarget.style.opacity = "0";
      setTimeout(() => {
        this.statusTarget.innerHTML = "";
        this.statusTarget.style.opacity = "1";
      }, 300);
    }
  }

  showSuccessMessage() {
    if (this.hasStatusTarget) {
      this.statusTarget.innerHTML = `
        <div class="absolute top-0 left-0 right-0 bg-green-500 bg-opacity-90 text-white text-xs py-1 px-2 text-center">
          <div class="flex items-center justify-center space-x-2">
            <svg class="h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
            <span>Image processed successfully!</span>
          </div>
        </div>
      `;

      // Hide the success message after 3 seconds
      setTimeout(() => {
        this.hideProcessingStatus();
      }, 3000);
    }
  }
}
